import MdParser from "@ext/markdown/core/Parser/MdParser/MdParser";
import type { Mark, Node } from "prosemirror-model";

export const CONTAINER_INDENT = "  ";

export type NodeSerializerSpec = (
	state: MarkdownSerializerState,
	node: Node,
	parent: Node,
	index: number,
) => void | Promise<void>;

export type MarkSerializerSpec = {
	/// The string that should appear before a piece of content marked
	/// by this mark, either directly or as a function that returns an
	/// appropriate string.
	open:
		| string
		| ((state: MarkdownSerializerState, mark: Mark, parent: Node, index: number) => Promise<string> | string);
	/// The string that should appear after a piece of content marked by
	/// this mark.
	close:
		| string
		| ((state: MarkdownSerializerState, mark: Mark, parent: Node, index: number) => Promise<string> | string);
	/// When `true`, this indicates that the order in which the mark's
	/// opening and closing syntax appears relative to other mixable
	/// marks can be varied. (For example, you can say `**a *b***` and
	/// `*a **b***`, but not `` `a *b*` ``.)
	mixable?: boolean;
	/// When enabled, causes the serializer to move enclosing whitespace
	/// from inside the marks to outside the marks. This is necessary
	/// for emphasis marks as CommonMark does not permit enclosing
	/// whitespace inside emphasis marks, see:
	/// http:///spec.commonmark.org/0.26/#example-330
	expelEnclosingWhitespace?: boolean;
	/// Can be set to `false` to disable character escaping in a mark. A
	/// non-escaping mark has to have the highest precedence (must
	/// always be the innermost mark).
	escape?: boolean;
	/// When it returns `true`, the mark is written as one piece that holds no other mark, as a link written `<url>`:
	/// the mixable marks on its text are written outside it.
	holdsNoMarks?: (state: MarkdownSerializerState, mark: Mark, parent: Node, index: number) => boolean;
};

export type GlobalFormatter = (
	state: MarkdownSerializerState,
	node: Node | Mark,
	parent?: Node,
	index?: number,
) => void;

/// A specification for serializing a ProseMirror document as
/// Markdown/CommonMark text.
export class MarkdownSerializer {
	/// Construct a serializer with the given configuration. The `nodes`
	/// object should map node names in a given schema to function that
	/// take a serializer state and such a node, and serialize the node.
	constructor(
		/// The node serializer functions for this serializer.
		readonly nodes: { [node: string]: NodeSerializerSpec },
		/// The mark serializer info.
		readonly marks: { [mark: string]: MarkSerializerSpec },

		readonly globalFormatters: GlobalFormatter[] = [],

		readonly options: {
			/// Extra characters can be added for escaping. This is passed
			/// directly to String.replace(), and the matching characters are
			/// preceded by a backslash.
			escapeExtraCharacters?: RegExp;
		} = {},
	) {}

	/// Serialize the content of the given node to
	/// [CommonMark](http://commonmark.org/).
	async serialize(
		content: Node,
		options: {
			/// Whether to render lists in a tight style. This can be overridden
			/// on a node level by specifying a tight attribute on the node.
			/// Defaults to false.
			tightLists?: boolean;
			escapeExtraCharacters?: RegExp;
		} = {},
		delim = "",
	) {
		Object.assign(this.options, options);
		const state = new MarkdownSerializerState(this.nodes, this.marks, this.globalFormatters, this.options, delim);
		await state.renderContent(content);
		return state.out;
	}
}
export class MarkdownSerializerState {
	/// @internal
	delim = "";
	/// @internal
	out = "";
	/// @internal
	closed: Node | null = null;
	/// @internal
	inAutolink: boolean | undefined = undefined;
	/// @internal
	atBlockStart = false;
	/// @internal
	inTightList = false;
	/// @internal
	private _listMarkers = new WeakMap<Node, string>();

	/// @internal
	constructor(
		/// @internal
		readonly nodes: { [node: string]: NodeSerializerSpec },
		/// @internal
		readonly marks: { [mark: string]: MarkSerializerSpec },
		/// @internal
		readonly globalFormatters: GlobalFormatter[] = [],
		/// The options passed to the serializer.
		readonly options: { tightLists?: boolean; escapeExtraCharacters?: RegExp },

		delim: string,
	) {
		if (typeof this.options.tightLists === "undefined") this.options.tightLists = false;
		this.delim = delim ?? "";
	}

	/// @internal
	flushClose(size = 2) {
		if (this.closed) {
			if (!this.atBlank()) this.out += "\n";
			if (size > 1) {
				let delimMin = this.delim;
				const trim = /\s+$/.exec(delimMin);
				if (trim) delimMin = delimMin.slice(0, delimMin.length - trim[0].length);
				for (let i = 1; i < size; i++) this.out += `${delimMin}\n`;
			}
			this.closed = null;
		}
	}

	async wrapContainer(
		openTag: string,
		closeTag: string,
		node: Node,
		renderBody: () => void | Promise<void>,
		indent = true,
	) {
		if (!indent) {
			this.write(`${openTag}\n\n`);
			await this.renderContent(node);
			this.write(closeTag);
			this.closeBlock(node);
			return;
		}

		await this.writeContainer(openTag, closeTag, renderBody);
		this.closeBlock(node);
	}

	/// The tags on lines of their own and the body one level deeper, for a body that closes its own blocks.
	async writeContainer(openTag: string, closeTag: string, renderBody: () => void | Promise<void>) {
		this.write(`${openTag}\n`);
		const old = this.delim;
		this.delim += CONTAINER_INDENT;
		await renderBody();
		this.flushClose(1);
		this.delim = old;
		this.write(closeTag);
	}

	/// Render a block, prefixing each line with `delim`, and the first
	/// line in `firstDelim`. `node` should be the node that is closed at
	/// the end of the block, and `f` is a function that renders the
	/// content of the block.
	async wrapBlock(delim: string, firstDelim: string | null, node: Node, f: () => void | Promise<void>) {
		const old = this.delim;
		this.write(firstDelim || delim);
		this.delim += delim;
		await f();
		this.delim = old;
		this.closeBlock(node);
	}

	/// @internal
	atBlank() {
		return /(^|\n)$/.test(this.out);
	}

	/// Ensure the current content ends with a newline.
	ensureNewLine() {
		if (!this.atBlank()) this.out += "\n";
	}

	/// Prepare the state for writing output (closing closed paragraphs,
	/// adding delimiters, and so on), and then optionally add content
	/// (unescaped) to the output.
	write(content?: string) {
		this.flushClose();
		if (this.delim && this.atBlank()) this.out += this.delim;
		if (content) this.out += content;
	}

	/// Close the block for the given node.
	closeBlock(node: Node) {
		this.processGlobalFormatters(node);
		this.closed = node;
	}

	/// Add the given text to the document. When escape is not `false`,
	/// it will be escaped; `typography` escapes what the reader turns into arrows and dashes as well.
	text(text: string, escape = true, typography = false) {
		const lines = text.split("\n");
		for (let i = 0; i < lines.length; i++) {
			this.write();
			// Escape exclamation marks in front of links
			// eslint-disable-next-line no-useless-escape
			if (!escape && lines[i][0] === "[" && /(^|[^\\])!$/.test(this.out))
				this.out = `${this.out.slice(0, this.out.length - 1)}\\!`;
			this.out += escape ? this.esc(lines[i], this.atBlockStart, typography) : lines[i];
			if (i !== lines.length - 1) this.out += "\n";
		}
	}

	/// Render the given node as a block.
	async render(node: Node, parent: Node, index: number) {
		if (typeof parent === "number") throw new Error("!");
		if (!this.nodes[node.type.name])
			throw new Error(`Token type \`${node.type.name}\` not supported by Markdown renderer`);
		await this.nodes[node.type.name](this, node, parent, index);
		if (node.type.name === "table_simple") this.out = formatTable(this.out, this.delim);
	}

	/// Blank lines inside a container separate blocks of content, not its structural children.
	async renderStructuralChildren(parent: Node) {
		for (let i = 0; i < parent.childCount; i++) {
			if (i) this.flushClose(1);
			await this.render(parent.child(i), parent, i);
		}
	}

	/// Render the contents of `parent` as block nodes.
	async renderContent(parent: Node) {
		// parent.forEach((node, _, i) => this.render(node, parent, i));
		for (let i = 0; i < parent.childCount; i++) {
			const child = parent.child(i);
			await this.render(child, parent, i);
		}
	}

	/// @internal
	processGlobalFormatters(node: Node | Mark, parent?: Node, index?: number) {
		for (const formatter of this.globalFormatters) {
			formatter(this, node, parent, index);
		}
	}

	/// Render the contents of `parent` as inline content.
	async renderInline(parent: Node) {
		this.atBlockStart = true;
		const active: Mark[] = [];
		let trailing = "";
		const progress = async (node: Node | null, _: number, index: number) => {
			let curNode = node;
			let marks = curNode ? curNode.marks : [];

			// Remove marks from `hard_break` that are the last node inside
			// that mark to prevent parser edge cases with new lines just
			// before closing marks.
			// (FIXME it'd be nice if we had a schema-agnostic way to
			// identify nodes that serialize as hard breaks)
			if (curNode && curNode.type.name === "hard_break")
				marks = marks.filter((m) => {
					if (index + 1 === parent.childCount) return false;
					const next = parent.child(index + 1);
					return m.isInSet(next.marks) && (!next.isText || /\S/.test(next.text));
				});

			let leading = trailing;
			trailing = "";
			// If whitespace has to be expelled from the node, adjust
			// leading and trailing accordingly.
			if (
				curNode?.isText &&
				marks.some((mark) => {
					const info = this.marks[mark.type.name];
					return info?.expelEnclosingWhitespace;
				})
			) {
				const [, lead, inner, trail] = /^(\s*)(.*?)(\s*)$/m.exec(curNode.text);
				leading += lead;
				trailing = trail;
				if (lead || trail) {
					// biome-ignore lint/suspicious/noExplicitAny: it`s TextNode, TextNode have this method
					curNode = inner ? (curNode as any).withText(inner) : null;
					if (!curNode) marks = active;
				}
			}

			// The schema ranks `code` below `strong`, so the non-escaping mark is put innermost here, not found last.
			const inner = marks.find((mark) => this.marks[mark.type.name].escape === false);
			const noEsc = !!inner;
			if (noEsc) marks = marks.filter((mark) => mark !== inner).concat(inner);
			const len = marks.length - (noEsc ? 1 : 0);

			marks = this.nestAroundLink(marks, len, active, parent, index);

			// Try to reorder 'mixable' marks, such as em and strong, which
			// in Markdown may be opened and closed in different order, so
			// that order of the marks for the token matches the order in
			// active.
			outer: for (let i = 0; i < len; i++) {
				const mark = marks[i];
				if (!this.marks[mark.type.name].mixable) break;
				for (let j = 0; j < active.length; j++) {
					const other = active[j];
					if (!this.marks[other.type.name].mixable) break;
					if (mark.eq(other)) {
						if (i > j)
							marks = marks
								.slice(0, j)
								.concat(mark)
								.concat(marks.slice(j, i))
								.concat(marks.slice(i + 1, len));
						else if (j > i)
							marks = marks
								.slice(0, i)
								.concat(marks.slice(i + 1, j))
								.concat(mark)
								.concat(marks.slice(j, len));
						continue outer;
					}
				}
			}

			// Find the prefix of the mark set that didn't change
			let keep = 0;
			while (keep < Math.min(active.length, len) && marks[keep].eq(active[keep])) ++keep;

			// Close the marks that need to be closed
			while (keep < active.length) this.text(await this.markString(active.pop(), false, parent, index), false);

			// Output any previously expelled trailing whitespace outside the marks
			if (leading) this.text(leading);

			// Open the marks that need to be opened
			if (curNode) {
				while (active.length < len) {
					const add = marks[active.length];
					active.push(add);
					this.text(await this.markString(add, true, parent, index), false);
				}

				// Render the node. Special case code marks, since their content
				// may not be escaped.
				if (noEsc && curNode.isText)
					this.text(
						this.markString(inner, true, parent, index) +
							this.escapeExtra(curNode.text) +
							this.markString(inner, false, parent, index + 1),
						false,
					);
				else await this.render(curNode, parent, index);
			}
		};
		// parent.forEach(progress);
		for (let i = 0, p = 0; i < parent.childCount; i++) {
			const child = parent.child(i);
			await progress(child, p, i);
			p += child.nodeSize;
		}
		await progress(null, 0, parent.childCount);
		this.atBlockStart = false;
	}

	/// A link closed and reopened becomes two links, a mixable mark stays one. So a mark that outlasts the link, or
	/// is already open around it, is written outside it; one that ends inside it is written inside.
	nestAroundLink(marks: readonly Mark[], len: number, active: readonly Mark[], parent: Node, index: number) {
		const isLink = (mark: Mark) =>
			!this.marks[mark.type.name].mixable && this.marks[mark.type.name].escape !== false;
		const linkAt = marks.slice(0, len).findIndex(isLink);
		if (linkAt < 0) return marks;
		const link = marks[linkAt];

		const lastIndex = (mark: Mark) => {
			let last = index;
			while (last + 1 < parent.childCount && mark.isInSet(parent.child(last + 1).marks)) last++;
			return last;
		};
		const activeAt = (mark: Mark) => active.findIndex((other) => other.eq(mark));
		const linkEnd = lastIndex(link);
		const isOutside = (mark: Mark, at: number) => {
			if (!this.marks[mark.type.name].mixable) return at < linkAt;
			if (activeAt(link) >= 0) return activeAt(mark) >= 0 && activeAt(mark) < activeAt(link);
			const end = lastIndex(mark);
			if (activeAt(mark) >= 0) return end >= linkEnd;
			if (end !== linkEnd) return end > linkEnd;
			return at < linkAt || (this.marks[link.type.name].holdsNoMarks?.(this, link, parent, index) ?? false);
		};

		const others = marks.slice(0, len).map((mark, at) => ({ mark, outside: isOutside(mark, at) }));
		return [
			...others.filter(({ mark, outside }) => outside && mark !== link).map(({ mark }) => mark),
			link,
			...others.filter(({ mark, outside }) => !outside && mark !== link).map(({ mark }) => mark),
			...marks.slice(len),
		];
	}

	/// Render a node's content as a list. `delim` should be the extra
	/// indentation added to all lines except the first in an item,
	/// `firstDelim` is a function going from an item index to a
	/// delimiter for the first line of the item.
	// The legacy `{% table %}` renders its cells through here, and there the blank line between
	// items separates cells: written tight, the table reads back as one cell. It asks for `tight = false`.
	async renderList(
		node: Node,
		delim: (index: number) => string,
		firstDelim: (index: number, otherMarker: boolean) => string,
		tight = true,
	) {
		// CommonMark starts a new list only on a new marker: two lists with one marker read back as one, blank lines or not.
		const markerChar = (otherMarker: boolean) => firstDelim(0, otherMarker).trim().slice(-1);
		const otherMarker = this.closed !== null && this._listMarkers.get(this.closed) === markerChar(false);
		this._listMarkers.set(node, markerChar(otherMarker));
		if (this.inTightList) this.flushClose(1);

		const isTight = tight;
		const prevTight = this.inTightList;
		this.inTightList = isTight;

		// node.forEach((child, _, i) => {
		// 	if (i && isTight) this.flushClose(1);
		// 	this.wrapBlock(delim(i), firstDelim(i), node, async () => await this.render(child, node, i));
		// });

		for (let i = 0; i < node.childCount; i++) {
			const child = node.child(i);
			if (i && isTight) this.flushClose(1);
			await this.wrapBlock(
				delim(i),
				firstDelim(i, otherMarker),
				node,
				async () => await this.render(child, node, i),
			);
		}

		this.inTightList = prevTight;
	}

	/// Escape the given string so that it can safely appear in Markdown
	/// content. If `startOfLine` is true, also escape characters that
	/// have special meaning only at the start of the line.
	// A `|` left bare even inside a code mark splits a Markdown table row on the next read.
	escapeExtra(str: string) {
		const extra = this.options.escapeExtraCharacters;
		return extra ? str.replace(extra, "\\$&") : str;
	}

	esc(str: string, startOfLine = false, typography = false) {
		let newStr = MarkdownSerializerState.escape(str);
		// `MdParser` reads `->`, `-->` and `--` in text as `→` and `—`: a literal one is written escaped.
		if (typography) newStr = newStr.replace(/(?<!-)--(?![->])|->/g, "\\$&");
		if (startOfLine) newStr = newStr.replace(/^[#\-*+>]/, "\\$&").replace(/^(\s*\d+)([.)])/, "$1\\$2");
		if (this.options.escapeExtraCharacters) newStr = newStr.replace(this.options.escapeExtraCharacters, "\\$&");
		return newStr;
	}

	/// @internal
	quote(str: string) {
		const wrap = str.indexOf('"') === -1 ? '""' : str.indexOf("'") === -1 ? "''" : "()";
		return wrap[0] + str + wrap[1];
	}

	/// Repeat the given string `n` times.
	repeat(str: string, n: number) {
		let out = "";
		for (let i = 0; i < n; i++) out += str;
		return out;
	}

	/// Get the markdown string for a given opening or closing mark.
	markString(mark: Mark, open: boolean, parent: Node, index: number) {
		const info = this.marks[mark.type.name];
		const value = open ? info.open : info.close;
		return typeof value === "string" ? value : value(this, mark, parent, index);
	}

	/// Get leading and trailing whitespace from a string. Values of
	/// leading or trailing property of the return object will be undefined
	/// if there is no match.
	getEnclosingWhitespace(text: string): { leading?: string; trailing?: string } {
		return {
			leading: (text.match(/^(\s+)/) || [undefined])[0],
			trailing: (text.match(/(\s+)$/) || [undefined])[0],
		};
	}

	static escape(str: string) {
		// `_` inside a word isn't emphasis (CommonMark/markdown-it treat intra-word
		// `_` as literal for any Unicode letter), so it must stay unescaped. The
		// intra-word test uses a Unicode class, not `\w` (ASCII-only), otherwise
		// Cyrillic words like `сло_во` get a spurious `\_` on save.
		const isWordChar = (ch: string) => /[\p{L}\p{N}_]/u.test(ch);
		const newStr = str.replace(/[`*\\~[\]_${<]/g, (m, i) =>
			m === "_" && i > 0 && i + 1 < str.length && isWordChar(str[i - 1]) && isWordChar(str[i + 1]) ? m : `\\${m}`,
		);
		return newStr;
	}
}

const splitTableRow = (line: string) => {
	const parts = [];
	let current = "";

	for (let i = line.indexOf("|") + 1; i < line.length - 1; i++) {
		const char = line[i];

		if (char === "|") {
			let backslashCount = 0;
			let j = i - 1;

			while (j >= 0 && line[j] === "\\") {
				backslashCount++;
				j--;
			}

			if (backslashCount % 2 === 0) {
				parts.push(current.trim());
				current = "";
				continue;
			}
		}
		current += char;
	}

	parts.push(current);
	return parts;
};

// No padding to the widest value: it would rewrite every row whenever one cell grows.
export const formatTable = (table: string, delim: string) => {
	const newTable = new MdParser().backParse(table);
	const lines = newTable.trim().split("\n");
	const [header, separator, ...rows] = lines;

	const writeRow = (line: string) =>
		`${delim}| ${splitTableRow(line)
			.map((cell) => cell.trim())
			.join(" | ")} |`;

	return [writeRow(header).slice(delim.length), `${delim}${separator.trim()}`, ...rows.map(writeRow)].join("\n");
};
